import {
  useIssuePermissions,
  useRelationshipTypes,
} from '@issuemanager/client/features/issues';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { GetUserRolesResponseDto } from '@issuemanager/api-client';
import {
  APP_ERROR_TYPE,
  ERROR_USER_INTERFACE,
  useAppErrorHandler,
} from '@issuemanager/apps/client/contexts';
import { ExtendedAxiosError } from '@issuemanager/apps/client/hooks';
import { paths } from '@issuemanager/apps/client/paths';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { IssueStatusEnum } from '@issuemanager/constants';
import { UseQueryResult } from '@tanstack/react-query';

export default function useIssueAuthorization(
  code: string | undefined,
  issueStatus?: string,
): {
  userRoles: UseQueryResult<GetUserRolesResponseDto, ExtendedAxiosError>;
  canManage: boolean | undefined;
  canViewRelationships: boolean | undefined;
} {
  const appErrorHandler = useAppErrorHandler();
  const isIssueRelationshipsEnabled = useFeatureFlags('issueRelationships');
  const { pathname } = useLocation();

  const issuePermissions = useIssuePermissions(code);
  const { types: relationshipTypes, isLoading: isLoadingRelationshipTypes } =
    useRelationshipTypes();

  // Combined loading state to avoid duplication
  const isLoading = issuePermissions.isLoading || isLoadingRelationshipTypes;

  const canManage =
    issuePermissions.data?.isCreator ||
    issuePermissions.data?.isIssueTypeManager ||
    issuePermissions.data?.isManager ||
    issuePermissions.data?.isOwner ||
    issuePermissions.data?.isActionOwner ||
    issuePermissions.data?.isApprover ||
    issuePermissions.data?.isActionApprover;

  const canViewRelationships = useMemo(() => {
    if (
      !isIssueRelationshipsEnabled ||
      !issuePermissions.data ||
      !issueStatus ||
      isLoadingRelationshipTypes
    ) {
      return undefined;
    }

    if (relationshipTypes.length === 0) {
      return false;
    }

    const { isManager, isIssueTypeManager, isOwner, isCreator } =
      issuePermissions.data;
    const isDraft = issueStatus === IssueStatusEnum.DRAFT;

    if (isManager || isIssueTypeManager || isOwner) {
      return true;
    }

    const isReporter =
      isCreator && !isManager && !isIssueTypeManager && !isOwner;
    return isReporter && isDraft;
  }, [
    isIssueRelationshipsEnabled,
    issuePermissions.data,
    issueStatus,
    relationshipTypes.length,
    isLoadingRelationshipTypes,
  ]);

  useEffect(() => {
    if (isLoading) return;

    if (!canManage && pathname.includes(paths.issues.manage)) {
      appErrorHandler.setError({
        type: APP_ERROR_TYPE.UNAUTHORIZED_TO_MANAGE_ISSUE,
        userInterface: ERROR_USER_INTERFACE.PAGE,
      });
    }

    if (
      canViewRelationships === false &&
      pathname.includes(paths.issues.relationships)
    ) {
      appErrorHandler.setError({
        type: APP_ERROR_TYPE.UNAUTHORIZED_TO_VIEW_RELATIONSHIPS,
        userInterface: ERROR_USER_INTERFACE.PAGE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, canManage, canViewRelationships, pathname]);

  return {
    userRoles: issuePermissions,
    canManage,
    canViewRelationships,
  };
}
